import type { FC, ReactNode } from 'react'
import { Users, Mail, Send, EyeOff } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button, Loader } from '@/components/ui'
import { useSubscriberStats } from '@/lib/api/hooks/subscriber'

interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
	description?: string
	descriptionClassName?: string
}

interface StatConfig {
	key: string
	title: string
	icon: LucideIcon
	iconContainerClassName: string
	iconClassName: string
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
	description,
	descriptionClassName,
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-10 w-10 xl:w-12 xl:h-12 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
					{description && <p className={`mt-1 text-sm ${descriptionClassName ?? 'text-green-700'}`}>{description}</p>}
				</div>
			</div>
		</article>
	)
}

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'totalSubscribers',
		title: 'Total Subscribers',
		icon: Users,
		iconContainerClassName: 'bg-[#F8E8EB80]',
		iconClassName: 'text-[#8F2F44]',
	},
	{
		key: 'activeSubscribers',
		title: 'Active Subscribers',
		icon: Mail,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	},
	{
		key: 'newSubscribers',
		title: 'New This Month',
		icon: Send,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#C2650A]',
	},
	{
		key: 'unSubscribers',
		title: 'Unsubscribed',
		icon: EyeOff,
		iconContainerClassName: 'bg-[#EDE9FE80]',
		iconClassName: 'text-[#6D28D9]',
	},
]

const SubscribersStats: FC = () => {
	const { data: stats, isLoading, isError, refetch } = useSubscriberStats()

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-4">
				<p>Error loading statistics.</p>
				<Button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded" loading={isLoading}>
					Retry
				</Button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center h-20 justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const totalCount = stats?.totalSubscribers ?? 0
	const activeCount = stats?.activeSubscribers ?? 0
	const unsubscribedCount = stats?.inactiveSubscribers ?? 0

	const values = {
		totalSubscribers: totalCount,
		activeSubscribers: activeCount,
		newSubscribers: stats?.newThisMonthSubscribers ?? 0,
		unSubscribers: unsubscribedCount,
	}

	const getDescription = (key: string) => {
		switch (key) {
			case 'totalSubscribers':
				return 'All time'
			case 'activeSubscribers': {
				const percentage = stats?.activePercentageOfTotal ?? 0
				return `${percentage.toFixed(2)}% of total`
			}
			case 'newSubscribers': {
				const change = stats?.newThisMonthPercentageChange ?? 0
				const direction = change < 0 ? '↓' : '↑'
				return `${direction} ${Math.abs(change)}% vs last month`
			}
			case 'unSubscribers': {
				const percentage = stats?.inactivePercentageOfTotal ?? 0
				return `${percentage.toFixed(2)}% of total`
			}
			default:
				return ''
		}
	}

	const getDescriptionClassName = (key: string) => {
		if (key === 'newSubscribers' && (stats?.newThisMonthPercentageChange ?? 0) < 0) {
			return 'text-red-600'
		}

		return 'text-green-700'
	}

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:w-3/4">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						title={config.title}
						value={values[config.key as keyof typeof values]}
						icon={<Icon className="w-6 h-6 xl:w-7 xl:h-7" strokeWidth={1.5} />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
						description={getDescription(config.key)}
						descriptionClassName={getDescriptionClassName(config.key)}
					/>
				)
			})}
		</section>
	)
}

export default SubscribersStats
